import {ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {FinancialPosition} from "../../models/financial-position";
import {FinancialPositionService} from "../../services/financial-position.service";
import {FormOption} from "../../../tandem-core/components/forms/form-option";
import {Subscription} from "rxjs";
import {AssetCategory} from "../../models/asset-category";
import {LiabilityCategory} from "../../models/liability-category";
import {ProposedChange} from "../../models/proposed-change";
import {AbstractProposedChangeItem} from "../../models/abstract-proposed-change-item";
import {Liability} from "../../models/liability";
import {Asset} from "../../models/asset";
import {DialogService, TandemDialogConfig} from "../../../tandem-core/services/dialog.service";
import {PcDisclosureComponent} from "./pc-disclosure/pc-disclosure.component";

@Component({
  selector: 'tandem-propose-changes',
  templateUrl: './propose-changes.component.html',
  styleUrls: ['./propose-changes.component.scss']
})
export class ProposeChangesComponent implements OnInit, OnDestroy {

  @Input() financialPosition: FinancialPosition | null = null;

  formGroup: FormGroup = new FormGroup<any>({});

  assetCategoryOptions: FormOption[] = [];
  liabilityCategoryOptions: FormOption[] = [];

  typeOptions: FormOption[] = [
    {
      label: 'Asset',
      value: 'Asset'
    },
    {
      label: 'Liability',
      value: 'Liability'
    },
  ];

  sourceCategoryOptions: FormOption[] = [];
  sourceItemOptions: FormOption[] = [];
  destinationCategoryOptions: FormOption[] = [];
  destinationItemOptions: FormOption[] = [];

  subscriptions: Subscription[] = [];
  @Output() formReset = new EventEmitter<void>();


  constructor(private fb: FormBuilder,
              private financialPositionService: FinancialPositionService,
              private cdr: ChangeDetectorRef,
              private dialog: DialogService) {
  }

  ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

  ngOnInit(): void {
    this.formGroup = this.fb.group({
      sourceType: [{value: null, disabled: false}, Validators.required],
      sourceCategory: [{value: null, disabled: true}, Validators.required],
      sourceItem: [{value: null, disabled: true}, Validators.required],
      amount: [{value: null, disabled: true}, Validators.required],
      destinationType: [{value: null, disabled: true}, Validators.required],
      destinationCategory: [{value: null, disabled: true}, Validators.required],
      destinationItem: [{value: null, disabled: true}, Validators.required],
    });

    this.registerSubscriptions()
  }

  private showNoChangeDialog() {
    let config: TandemDialogConfig = {
      title: 'Nothing Changed',
      content: `No proposed change will be added since the proposed amount is $0`,
      type: 'info',
      actions: [
        {
          text: 'Okay',
          callback: () => this.dialog.closeModal2(),
          role: 'close'
        },
      ]
    }
    this.dialog.openModal2(config);
  }

  onSubmit() {
    let sourceItem: Asset | Liability = this.formGroup.get('sourceItem')?.value;
    let destinationItem: Asset | Liability = this.formGroup.get('destinationItem')?.value;
    let sourceType: 'Asset' | 'Liability' = this.formGroup.get('sourceType')?.value;
    let destinationType: 'Asset' | 'Liability' = this.formGroup.get('destinationType')?.value;

    const proposedChange: ProposedChange = {
      id: Math.floor(Math.random() * 10000) + 1,
      completed: false,
      sourceItem: sourceItem.name,
      destinationItem: destinationItem.name,
      amount: Number(this.formGroup.get('amount')?.value),
      destinationCategory: this.formGroup.get('destinationCategory')?.value.name,
      destinationType: this.formGroup.get('destinationType')?.value,
      sourceType: this.formGroup.get('sourceType')?.value,
      sourceCategory: this.formGroup.get('sourceCategory')?.value.name
    };

    let sufficientSourceBalance = true;
    let sufficientDestinationBalance = true;

    let currentSourceAmount = this.getTotalProposedChange(sourceType, sourceItem);
    let currentDestinationAmount = this.getTotalProposedChange(destinationType, destinationItem);


    if (sourceType === 'Asset' && currentSourceAmount < proposedChange.amount) {
      sufficientSourceBalance = false;
    }

    if (destinationType === 'Liability' && currentDestinationAmount < proposedChange.amount) {
      sufficientDestinationBalance = false;
    }

    if (!sufficientSourceBalance) {
      proposedChange.amount = currentSourceAmount;
      let modalConfig: TandemDialogConfig = {
        title: 'Insufficient Funds',
        type: 'info',
        content: `Looks like the amount of funds you're attempting to move from your source exceeds the total outstanding amount of your destination. Your proposed change will automatically correct to equal the total outstanding amount of your destination.`,
        actions: [
          {
            text: 'Okay',
            role: 'primary',
            callback: () => {
              this.dialog.closeModal2();
              if (!sufficientDestinationBalance) {
                let lessFundsConfig: TandemDialogConfig = {
                  title: 'Move Less Funds',
                  content: `It looks like you're attempting to transfer an amount that exceeds the total balance of your destination. Your proposed change will automatically be set to the correct amount.`,
                  type: "info",
                  actions: [
                    {
                      text: 'Okay',
                      role: "primary",
                      callback: () => {
                        this.dialog.closeModal2();
                        proposedChange.amount = Math.min(currentSourceAmount, currentDestinationAmount);
                        this.saveChange(proposedChange, sourceItem, destinationItem, sourceType, destinationType);
                      }
                    }
                  ]
                }
                this.dialog.openModal2(lessFundsConfig);
              } else {
                this.saveChange(proposedChange, sourceItem, destinationItem, sourceType, destinationType);
              }
            }
          }
        ]
      }
      this.dialog.openModal2(modalConfig);
    } else if (!sufficientDestinationBalance) {
      let excessiveTransferConfig: TandemDialogConfig = {
        title: 'Excessive Transfer',
        content: `It looks like you're attempting to transfer an amount that exceeds the total balance of your destination. Your proposed change will automatically be set to the correct amount.`,
        type: "info",
        actions: [
          {
            text: 'Okay',
            role: 'primary',
            callback: () => {
              proposedChange.amount = Math.min(currentSourceAmount, currentDestinationAmount);
              this.saveChange(proposedChange, sourceItem, destinationItem, sourceType, destinationType);
            }
          }
        ]
      }
      this.dialog.openModal2(excessiveTransferConfig);
    }
    else {
      this.saveChange(proposedChange, sourceItem, destinationItem, sourceType, destinationType);
    }
  }

  private getTotalProposedChange(itemType: 'Asset' | 'Liability', item: Asset | Liability): number {
    let totalChange = 0;

    if (itemType === 'Asset') {
      item.proposedChanges?.forEach(change => {
        if (change.sourceItem === item.name) {
          totalChange -= change.amount;
        } else if (change.destinationItem === item.name) {
          totalChange += change.amount;
        }
      })
    } else { // Liability
      item.proposedChanges?.forEach(change => {
        if (change.sourceItem === item.name) {
          totalChange += change.amount;
        } else if (change.destinationItem === item.name) {
          totalChange -= change.amount;
        }
      })
    }
    return item.value + totalChange;
  }

  private saveChange(proposedChange: ProposedChange,
                     sourceItem: Asset | Liability,
                     destinationItem: Asset | Liability,
                     sourceType: 'Asset' | 'Liability',
                     destinationType: 'Asset' | 'Liability') {

    if (proposedChange.amount <= 0) {
      this.showNoChangeDialog();
    } else {
      if (!sourceItem.proposedChanges) {
        sourceItem.proposedChanges = [];
      }
      if (!destinationItem.proposedChanges) {
        destinationItem.proposedChanges = [];
      }

      sourceItem.proposedChanges.push(proposedChange);
      destinationItem.proposedChanges.push(proposedChange);


      if (this.financialPosition) {
        // this.financialPosition.proposedChanges!.push(proposedChange);
        this.financialPositionService.update(this.financialPosition).then(res => {
          this.formGroup.reset();
          this.formGroup.get('sourceType')?.setValue(null);
          this.formReset.emit();
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        }).catch(err => {
          console.error(err);
        })
        this.formGroup.get('sourceType')?.setValue(null);
        this.formReset.emit();
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }
    }
  }

  private registerSubscriptions() {
    this.subscriptions.push(this.formGroup.get('sourceType')!.valueChanges.subscribe(val => {
      this.sourceCategoryOptions = [];
      if (val === 'Asset') {
        this.financialPosition?.assets.forEach(a => this.sourceCategoryOptions.push({
          label: a.name,
          value: a
        }));
      } else if (val === 'Liability') {
        this.financialPosition?.liabilities.forEach(a => this.sourceCategoryOptions.push({
          label: a.name,
          value: a
        }));
      }
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      this.formGroup.get('sourceCategory')?.reset();
      this.formGroup.get('sourceCategory')?.enable();
      this.formGroup.get('sourceItem')?.reset();
      this.formGroup.get('sourceItem')?.disable();
      this.formGroup.get('amount')?.reset();
      this.formGroup.get('amount')?.disable();
      this.formGroup.get('destinationType')?.reset();
      this.formGroup.get('destinationType')?.disable();
      this.formGroup.get('destinationCategory')?.reset();
      this.formGroup.get('destinationCategory')?.disable();
      this.formGroup.get('destinationItem')?.reset();
      this.formGroup.get('destinationItem')?.disable();
    }));

    this.subscriptions.push(this.formGroup.get('sourceCategory')!.valueChanges.subscribe((val: AssetCategory | LiabilityCategory | null) => {
      if (val) {
        this.sourceItemOptions = [];
        if (this.formGroup.get('sourceType')?.value === 'Asset') {
          const cat: AssetCategory = val as AssetCategory;
          cat.assets.forEach(asset => {this.sourceItemOptions.push({
            label: asset.name,
            value: asset
          })})
        } else if (this.formGroup.get('sourceType')?.value === 'Liability') {
          const cat: LiabilityCategory = val as LiabilityCategory;
          cat.liabilities.forEach(liability => {this.sourceItemOptions.push({
            label: liability.name,
            value: liability
          })})
        }
      }

      this.formGroup.get('sourceItem')?.reset();
      this.formGroup.get('sourceItem')?.enable();
      this.formGroup.get('amount')?.reset();
      this.formGroup.get('amount')?.disable();
      this.formGroup.get('destinationType')?.reset();
      this.formGroup.get('destinationType')?.disable();
      this.formGroup.get('destinationCategory')?.reset();
      this.formGroup.get('destinationCategory')?.disable();
      this.formGroup.get('destinationItem')?.reset();
      this.formGroup.get('destinationItem')?.disable();
    }));

    this.subscriptions.push(this.formGroup.get('sourceItem')!.valueChanges.subscribe((val: Asset | Liability | null) => {
      this.formGroup.get('amount')?.reset();
      this.formGroup.get('amount')?.enable();
      this.formGroup.get('destinationType')?.reset();
      this.formGroup.get('destinationType')?.disable();
      this.formGroup.get('destinationCategory')?.reset();
      this.formGroup.get('destinationCategory')?.disable();
      this.formGroup.get('destinationItem')?.reset();
      this.formGroup.get('destinationItem')?.disable();
    }));

    this.subscriptions.push(this.formGroup.get('amount')!.valueChanges.subscribe((val: number | null) => {
      this.formGroup.get('destinationType')?.reset();
      this.formGroup.get('destinationType')?.enable();
      this.formGroup.get('destinationCategory')?.reset();
      this.formGroup.get('destinationCategory')?.disable();
      this.formGroup.get('destinationItem')?.reset();
      this.formGroup.get('destinationItem')?.disable();
    }));

    this.subscriptions.push(this.formGroup.get('destinationType')!.valueChanges.subscribe(val => {
      this.destinationCategoryOptions = [];
      if (val === 'Asset') {
        this.financialPosition?.assets.forEach(a => this.destinationCategoryOptions.push({
          label: a.name,
          value: a
        }));
      } else if (val === 'Liability') {
        this.financialPosition?.liabilities.forEach(a => this.destinationCategoryOptions.push({
          label: a.name,
          value: a
        }));
      }
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      this.formGroup.get('destinationCategory')?.reset();
      this.formGroup.get('destinationCategory')?.enable();
      this.formGroup.get('destinationItem')?.reset();
      this.formGroup.get('destinationItem')?.disable();
    }));

    this.subscriptions.push(this.formGroup.get('destinationCategory')!.valueChanges.subscribe((val: AssetCategory | LiabilityCategory | null) => {
      if (val) {
        this.destinationItemOptions = [];
        if (this.formGroup.get('destinationType')?.value === 'Asset') {
          const cat: AssetCategory = val as AssetCategory;
          cat.assets.forEach(asset => {
            if (asset.name !== this.formGroup.get('sourceItem')?.value?.name) {
              this.destinationItemOptions.push({
                label: asset.name,
                value: asset
              });
            }
          })
        } else if (this.formGroup.get('destinationType')?.value === 'Liability') {
          const cat: LiabilityCategory = val as LiabilityCategory;
          cat.liabilities.forEach(liability => {
            if (liability.name !== this.formGroup.get('sourceItem')?.value?.name) {
              this.destinationItemOptions.push({
                label: liability.name,
                value: liability
              });
            }
          });
        }
      }
      this.formGroup.get('destinationItem')?.reset();
      this.formGroup.get('destinationItem')?.enable();
    }));
  }

  openDisclosureNotice() {
    this.dialog.openModal(PcDisclosureComponent, {});
  }
}
